// @flow
import React from 'react'
import styled, { css } from 'styled-components'
import { rgba } from 'polished'

import theme from 'live/services/theme'
import i18n from 'live/services/i18n'
import Label from 'live/components/forms/ui/label'
import LiveTooltip from 'live/components/ui/tooltip'
import SubmitOrCancel from 'live/components/forms/ui/submit-or-cancel'
import CardContainer, { ControlButton, Title } from 'live/components/ui/CardContainer'

type Props = {
    title: string,
    children: React$Node,
    toggleEdit?: () => void,
    onSubmit: () => void,
    onCancel?: () => void,
    onSubmitAndClose?: () => void,
    disabled: boolean,
    disableContent?: boolean,
    disableCancel?: boolean,
    collapsed?: boolean,
    editing?: boolean,
    canEdit: boolean,
    saving?: boolean,
    submitted?: boolean,
    saved?: boolean,
    // renderControls?: ({ collapsed: boolean, toggleCollapsed: boolean => void }) => React$Node,
    className?: string,
    stickyFooter?: boolean,
    hideCancel?: boolean,
    showSubmitAndClose?: boolean,
    hideRequired?: boolean,
    showSubmitAndClose?: boolean,
    disabledSubmitAndClose?: boolean,
    submitAndCloseSaving?: boolean,
    submitAndCloseSaved?: boolean,
    submitAndCloseMustSave?: boolean, // ToDo
    onSubmitAndClose?: () => void,
    tooltipData?: { text: string, position: 'left' | 'top' | 'right' | 'bottom' }
}

export const FieldRow = React.memo(function FieldRow(props) {
    return <StyledFieldRow {...props} />
})

export const Field = React.memo(function Field(props) {
    return <StyledField className={props.name} {...props} />
})

export const Fieldset = React.forwardRef<Props, HTMLDivElement>(function Fieldset(
    {
        title,
        children,
        toggleEdit,
        disabled,
        disableContent,
        disableCancel,
        collapsed,
        editing,
        saving,
        onSubmit,
        onCancel,
        canEdit,
        submitted,
        saved,
        renderControls,
        className,
        stickyFooter,
        hideCancel,
        hideRequired,
        showSubmitAndClose = false,
        disabledSubmitAndClose,
        submitAndCloseSaving,
        submitAndCloseSaved,
        submitAndCloseMustSave,
        onSubmitAndClose,
        tooltipData
    }: Props,
    ref
) {
    if (!canEdit) editing = false
    if (disableContent === undefined) disableContent = true

    return (
        <StyledCardContainer
            className={`${className || ''} fieldset-card-container`}
            editing={editing}
            title={title}
            collapsed={collapsed}
            renderControls={({ collapsed, toggleCollapsed }) => {
                if (renderControls) return renderControls({ collapsed, toggleCollapsed })

                if (editing) return null

                return (
                    <ContentButtons>
                        {tooltipData && <TooltipContent left={tooltipData.left}>
                            <LiveTooltip position={tooltipData.position} content={tooltipData.text}>
                                <i className="fa fa-question-circle-o"></i>
                            </LiveTooltip>
                        </TooltipContent>}
                        <RightButtons>
                            {canEdit && (
                                <ControlButton className="ml-12">
                                    <i
                                        onClick={() => {
                                            toggleEdit()
                                            if (collapsed) toggleCollapsed()
                                        }}
                                        className="far fa-edit"
                                    />
                                </ControlButton>
                            )}
                            <ControlButton className="ml-12">
                                <i onClick={toggleCollapsed} className={`far fa-angle-${collapsed ? 'down' : 'up'}`} />
                            </ControlButton>
                        </RightButtons>
                    </ContentButtons>
                )
            }}
        >
            <FieldsetContainer
                className="Fieldset__Container"
                innerRef={ref}
                editing={editing}
                disabled={disabled && disableContent}
            >
                {children}
            </FieldsetContainer>

            {editing ? (
                <FieldsetFooter stickyFooter={stickyFooter}>
                    <SubmitOrCancel
                        // save
                        disabled={disabled || !editing || submitted}
                        saving={saving}
                        saved={saved}
                        mustSave={!submitted}
                        onSubmit={onSubmit}
                        // save and close
                        showSubmitAndClose={showSubmitAndClose}
                        disabledSubmitAndClose={(disabled || !editing || submitted) && disabledSubmitAndClose}
                        submitAndCloseSaving={submitAndCloseSaving}
                        submitAndCloseSaved={submitAndCloseSaved}
                        submitAndCloseMustSave={!submitted}
                        onSubmitAndClose={onSubmitAndClose}
                        // cancel
                        disableCancel={(disabled || !editing || submitted) && disableCancel}
                        hideCancel={hideCancel}
                        onCancel={onCancel}
                    />
                    {!hideRequired && <RequiredNotice />}
                </FieldsetFooter>
            ) : (
                    <ViewFooter />
                )}
        </StyledCardContainer>
    )
})

export function EditIcon({
    position = 'top',
    toolTipContent = 'edit'
}: {
    position?: string,
    toolTipContent?: string
}) {
    return (
        <LiveTooltip position={position} content={i18n(toolTipContent)}>
            <StyledEditIcon className="far fa-edit" />
        </LiveTooltip>
    )
}

const StyledEditIcon = styled.i`
    color: ${theme['link-color']};
    font-size: 13px;
    cursor: pointer;
`

const INPUT_HEIGHT = `${28}px`
const TEXTAREA_MIN_HEIGHT = `60px`

export const InputContainer = styled.div`
    margin-top: -4px;
    ${({ textarea }) =>
        textarea &&
        css`
            min-height: ${TEXTAREA_MIN_HEIGHT};
        `}
    textarea,
    input,
    > span {
        display: block;
        box-shadow: none;
        border-radius: 0;
        padding: 0;
        line-height: 1;
        font-size: 13px;
        color: ${theme['auxiliar-typography']};
        width: 100%;
        border: 0;
        background: transparent;

        &:disabled {
            background: transparent;
        }
    }

    input {
        height: ${INPUT_HEIGHT};
    }

    textarea {
        height: ${TEXTAREA_MIN_HEIGHT};
    }

    .input-group-addon {
        background: transparent !important;
    }

    /* Select-control */
    .Select-control,
    .Select-input {
        height: ${INPUT_HEIGHT};
    }
    .Select-menu-outer {
        margin-top: 11px;
        display: grid;
    }
    .Select-placeholder {
        line-height: ${INPUT_HEIGHT};
        padding-left: 20px;
        font-size: 13px !important;
    }
    .Select--single,
    &.Select--single {
        margin-left: -20px;
        margin-right: -20px;
        > .Select-control .Select-value {
            line-height: ${INPUT_HEIGHT};
            padding-left: 20px;
        }
    }
    .Select-option {
        font-size: 13px !important;
        line-height: 12px !important;
        i {
            margin-top: -2px;
        }
        padding-left: 20px;
    }
    .Select-control {
        border: 0;
        box-shadow: none !important;
        background-color: inherit !important;
        font-size: 13px;
        padding-bottom: 5px;
    }
    .Select-input {
        padding-left: 20px;
    }
`

const focusedFieldColor = rgba(theme['blue-lighter'], 0.2)
const disabledColor = rgba(theme.gray, 0.5)
const warningColor = rgba(theme['hint-background'], 0.5)
const StyledField = styled.div`

    ${({validation}) =>{
        return validation &&
            css`
                background-color:${rgba(theme['dark-red'], 0.2)};
            `
    }}
    ${({ flex }) => {
        return flex && 
            css`
                flex: ${flex} !important;
            `
    }}
    ${({ focused }) =>
        focused
            ? css`
                  background-color: ${focusedFieldColor};
                  .rdw-editor-toolbar {
                      display: flex;
                  }
              `
            : css`
                  .rdw-editor-toolbar {
                      display: none;
                  }
              `}

    ${({ requiredFieldError }) =>
        requiredFieldError &&
        css`
            background: ${rgba(theme['dark-red'], 0.2)};
        `}

    ${({ fieldWarning }) =>
        fieldWarning &&
        css`
            background: ${warningColor};
        `}

    ${({ disabled }) =>
        disabled &&
        css`
            background-color: ${disabled ? '#eee' : '#fff'};
        `}

    @media print{
        display: block;
        float: none !important; 
        position: static !important; 
        box-sizing: content-box !important;

        page-break-after: avoid;
        page-break-before: auto;
        page-break-inside: avoid;
        break-after: avoid;
        break-before: auto;
        break-inside: avoid;

    }
`

export const RequiredNotice = () => <StyledLabel required>{i18n('Required')}</StyledLabel>

const border = `1px solid ${theme['gray-lighter']}`

const StyledLabel = styled(Label)`
    display: inline-block;
    margin-top: 9px;

    :after {
        padding-right: 2px;
    }
`

const FieldsetFooter = styled.footer`
    border-top: ${border};
    background-color: ${theme['gray-lighter']};
    padding-bottom: 12px;
    display: flex;
    justify-content: space-between;

    ${({ stickyFooter }) =>
        stickyFooter &&
        css`
            position: sticky;
            bottom: 0;
            z-index: 5;
        `}
`

const ViewFooter = styled.footer`
    && {
        padding: 0;
    }
    background-color: white;
`

export const FieldValue = ({ children, ...props }) => {
    return (
        <FieldValueContainer className="Fieldset__FieldValue">
            <StyledFieldValue {...props}>{children}</StyledFieldValue>
        </FieldValueContainer>
    )
}

const FieldValueContainer = styled.div`
    position: relative;
    width: 100%;
    min-height: ${INPUT_HEIGHT};
    font-size: 13px;
    line-height: 20px;
    display: grid;

    @media print {
        display: grid;
        float: none !important; 
        position: static !important; 
        box-sizing: content-box !important;

        page-break-after: avoid;
        page-break-before: auto;
        page-break-inside: avoid;
        break-after: avoid;
        break-before: auto;
        break-inside: avoid;
    }
`

const StyledFieldValue = styled.span`
    color: ${theme['auxiliar-typography']};
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;

    ${({ textarea }) =>
        textarea &&
        css`
            display: block;
            height: auto;
            align-items: flex-start;
            min-height: ${TEXTAREA_MIN_HEIGHT};
        `}

    @media print {
        display: block;
        float: none !important; 
        position: static !important; 
        box-sizing: content-box !important;

        page-break-after: avoid;
        page-break-before: auto;
        page-break-inside: avoid;
        break-after: avoid;
        break-before: auto;
        break-inside: avoid;

        * {
            float: none !important; 
            position: static !important; 
            box-sizing: content-box !important;
            
            page-break-after: avoid;
            page-break-before: auto;
            page-break-inside: avoid;
            break-after: avoid;
            break-before: auto;
            break-inside: avoid;
        }
    }
`
const StyledFieldRow = styled.div`
    display: flex;
    align-items: center;
    border-top: ${border};
    align-items: stretch;

    ${({ textarea }) =>
        textarea &&
        css`
            min-width: 0;
            overflow: auto;

            ${FieldValueContainer} {
                display: block;
                height: auto;
                align-items: flex-start;
                min-height: ${TEXTAREA_MIN_HEIGHT};
            }

            ${InputContainer} {
                min-height: ${TEXTAREA_MIN_HEIGHT};
            }
        `}

    > div {
        flex: 1;
        padding: 10px 20px;

        &:not(:last-of-type) {
            border-right: ${border};
        }

        label {
            font-family: ${theme['font-semibold']};
            font-size: 12px;
            display: flex;
            width: 100%;

            span:nth-of-type(1) {
            }

            > a {
                margin-left: 3px;
            }
            span:nth-of-type(2) {
                margin-left: 3px;
                color: ${theme.icons};
            }

            span:nth-of-type(3) {
                margin-left: 9px;
                color: ${theme['light-red']};
            }
        }
    }

    @media print{
        display: inline-flex;
        float: none !important; 
        position: static !important; 
        box-sizing: content-box !important;

        page-break-after: avoid;
        page-break-before: auto;
        page-break-inside: avoid;
        break-after: avoid;
        break-before: auto;
        break-inside: avoid;

        /* * {
            float: none !important; 
            position: static !important; 
            box-sizing: content-box !important;

            page-break-after: avoid;
            page-break-before: auto;
            page-break-inside: avoid;
            break-after: avoid;
            break-before: auto;
            break-inside: avoid;
        } */
    }
`

const FieldsetContainer = styled.fieldset`
    display: flex;
    flex-direction: column;
    min-width: 0;

    .react-datepicker-popper {
        z-index: 10;
    }

    .form-group {
        margin-bottom: 0;
    }

    && {
        padding: 0;
    }

    > header {
        margin-bottom: ${border};
        font-family: ${theme['font-semibold']};
        font-size: 14px;
    }

    ${StyledFieldRow}, .Fieldset__FieldRow {     
        > div {
            ${({ editing }) =>
        editing
            ? css`
                          ${FieldValueContainer}, .Fieldset__Value {
                              display: none !important;
                          }
                      `
            : css`
                          input,
                          textarea,
                          .form-group,
                          .Fieldset__InputContainer,
                          ${InputContainer} {
                              display: none !important;
                          }
                      `}
        }
    }
`

export const RequiredFieldIcon = styled.span`
    margin-left: 2px;
    color: ${theme['brand-danger']};
`

export const StyledSubSection = styled.div`
    > header {
        border-top: ${border};
        padding: 13px 20px 14px;

        > label:first-of-type {
            font-size: 14px;
        }
    }

    > ${StyledFieldRow}:first-of-type {
        border-top: none;
    }
`

export const StyledCardContainer = styled(CardContainer)`
    padding: 0;

    > header {
        > div:first-of-type {
            text-transform: uppercase;
        }
    }

    ${({ editing }) =>
        editing &&
        css`
            border: 1px solid ${theme['brand-primary']};
            && {
                && {
                    opacity: 1;
                    pointer-events: all;
                }
            }

            > header {
                background-color: ${theme['gray-lighter']};
                border-top-left-radius: 3px;
                border-top-right-radius: 3px;
            }
            > footer {
                border-bottom-left-radius: 3px;
                border-bottom-right-radius: 3px;
            }
        `}

    > * {
        padding: 13px 20px 14px;
    }

    ${Title} {
        text-transform: uppercase;
    }
    position:relative;

    display: block;

    @media print{
        float: none !important; 
        position: static !important; 
        box-sizing: content-box !important;
            
        page-break-after: avoid;
        page-break-before: auto;
        page-break-inside: avoid;
        break-after: avoid;
        break-before: auto;
        break-inside: avoid;
       

        /* * {            
            page-break-after: auto;
            page-break-before: auto;
            page-break-inside: auto;
            break-after: auto;
            break-before: auto;
            break-inside: auto;
        } */
    }
`
export const TabContainer = styled.div`
    .fieldset-card-container {
        transition: opacity 0.25s ease-out;
    }

    ${({ editing, readOnly }) =>
        editing &&
        !readOnly &&
        css`
            .fieldset-card-container {
                opacity: 0.5;
                pointer-events: none;
            }
        `}
    ${({ readOnly }) =>
        !readOnly &&
        css`
            .fieldset-card-container {
                margin-top: 28px;
            }
        `}
`

const TooltipContent = styled.div`
    position: absolute;
    left: 0;
    font-size: 14px;
    margin-top: 1px;
    margin-left: 10px;
    color: ${({ theme }) => theme['brand-primary']};
`
export const RightButtons = styled.div`
    position: absolute;
    right: 0;
    >div{
        display: inline-block;
    }
`
export const ContentButtons = styled.div`
    position:relative;
    width:100%;
`
Fieldset.displayName = 'Fieldset'

export const AddRow = styled.div`
    position: relative;
    z-index: 10;

    button.btn {
        font-family: ${theme['font-semibold']};
        color:${theme['brand-primary']};
        border-radius: 0;
        font-size: 13px;
        border-left: none;
        border-right: none;
        border:0;
        width:100%;
        background: #E1F0FF;
        /* border-color: ${theme=> theme['blue-200']}; */
    }
`
